import React, { useEffect, useRef } from "react";

const toc = [
  ["Scene Graph Overview", "overview"],
  ["Scene Graph Example", "example"],
  ["Modern Scene Graphs", "modern"],
  ["Matrix Stack Overview", "matrix-stack"],
  ["Matrix Stack Walkthrough", "stack-walkthrough"],
  ["Transforming Geometry", "transforming-geometry"],
  ["Immediate-Mode API", "immediate-mode"],
  ["Declarative Scene Graphs", "declarative"],
  ["Inverse Transforms", "inverse"],
  ["Tracking Inverse Approaches", "inverse-approaches"],
  ["Forward + Inverse Stack", "forward-inverse-stack"],
  ["Cross-Level Transforms", "cross-level"],
  ["Summary", "summary"],
];

const F = ({ children }) => (
  <div className="formula-block">
    {(Array.isArray(children) ? children : [children]).map((line, i) => (
      <div key={i} className="formula-row">{line}</div>
    ))}
  </div>
);

const NoteBox = ({ label = "Note", children }) => (
  <div className="note-box">
    <div className="note-label">{label}</div>
    {children}
  </div>
);

const SceneGraph = ({ renderToc }) => {
  const ref = useRef(null);

  useEffect(() => {
    const tryRender = () => {
      if (window.renderMathInElement && ref.current) {
        window.renderMathInElement(ref.current, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "$", right: "$", display: false },
          ],
          throwOnError: false,
        });
      } else {
        setTimeout(tryRender, 100);
      }
    };
    tryRender();
  }, []);

  useEffect(() => {
    if (renderToc) renderToc(toc);
  }, [renderToc]);

  return (
    <div ref={ref}>
      <h1>Scene Graphs and Matrix Stacks</h1>
      <p className="subtitle">by Jeff Wilson, Ph.D.</p>

      {/* ── OVERVIEW ── */}
      <h2 id="overview">Scene Graph Overview</h2>
      <p>
        A <strong>scene graph</strong> is a data structure that manages
        hierarchical spatial relationships and transformations in a complex 3D
        scene. Transformations applied to a parent node automatically propagate
        to all of its descendants.
      </p>
      <p>Key structural properties:</p>
      <ul>
        <li>
          <strong>Directed Acyclic Graph (DAG)</strong> — nodes have directed
          edges with no cycles
        </li>
        <li>
          Each node holds geometry (or an object) and a local transformation
          matrix
        </li>
        <li>
          Local coordinate transforms at any node are always defined{" "}
          <em>relative to the parent's coordinate system</em>
        </li>
        <li>
          Transformation matrices computed at each node via a matrix stack
          define the object's <strong>local-to-world transformation</strong>
        </li>
      </ul>

      {/* ── EXAMPLE ── */}
      <h2 id="example">Scene Graph Example — Room Hierarchy</h2>
      <p>
        Consider a kitchen scene with the following hierarchy:
        Room → Table → Saucer → Coffee Cup.
      </p>
      <ul>
        <li>
          {"$M_{\\text{room}}$"} — room's transform relative to world
        </li>
        <li>
          {"$M_{\\text{table}}$"} — table's transform relative to room
        </li>
        <li>
          {"$M_{\\text{saucer}}$"} — saucer's transform relative to table
        </li>
        <li>
          {"$M_{\\text{cup}}$"} — cup's transform relative to saucer
        </li>
      </ul>
      <p>
        The <strong>local-to-world transform</strong> for the coffee cup is the
        product of all ancestor transforms:
      </p>
      <F>
        {"$$M_{\\text{cup\\_to\\_world}} = M_{\\text{room}} \\cdot M_{\\text{table}} \\cdot M_{\\text{saucer}} \\cdot M_{\\text{cup}}$$"}
      </F>
      <p>
        Moving the table (updating {"$M_{\\text{table}}$"}) automatically moves
        everything on it — the saucer and cup follow without any explicit update.
      </p>

      {/* ── MODERN ── */}
      <h2 id="modern">Modern Scene Graphs</h2>
      <p>
        Rigid scene-wide hierarchies (e.g. kitchen → table → cup) are less
        common today because they are inflexible for applications such as
        physics simulation, where objects need to move independently in world
        coordinates.
      </p>
      <p>
        However, hierarchical relationships remain essential for{" "}
        <strong>compound objects</strong> — assemblies whose parts are animated
        independently but are spatially connected. Example:
      </p>
      <pre>{`robot_base → upper_arm → lower_arm → grabber`}</pre>
      <p>
        Each part animates independently yet inherits its parent's position and
        orientation. The hierarchy captures this spatial coupling naturally.
      </p>

      {/* ── MATRIX STACK ── */}
      <h2 id="matrix-stack">Matrix Stack Overview</h2>
      <p>
        A <strong>matrix stack</strong> facilitates hierarchical transformations
        during scene graph traversal. It maintains a stack of cumulative
        transformation matrices, allowing each branch of the graph to be
        processed independently.
      </p>
      <h3>Stack Operations</h3>
      <ul>
        <li>
          <code>pushMatrix()</code> — duplicates the top matrix and pushes the
          copy; the new copy becomes the active matrix
        </li>
        <li>
          <code>popMatrix()</code> — discards the top matrix, restoring the
          previous one
        </li>
      </ul>
      <NoteBox label="Important">
        Push and pop calls must <strong>always be balanced</strong>. Every{" "}
        <code>pushMatrix</code> must have a corresponding{" "}
        <code>popMatrix</code>.
      </NoteBox>
      <h3>Applying New Transformations</h3>
      <p>
        New transformations are multiplied onto the{" "}
        <strong>right side</strong> of the current top matrix:
      </p>
      <F>{"$$M_{\\text{new}} = M_{\\text{current}} \\cdot T$$"}</F>
      <p>
        Right-multiplication ensures that more local transformations are
        applied to an object <em>before</em> ascendant (parent) transformations
        — consistent with the column-vector convention where{" "}
        {"$\\mathbf{p}' = M\\cdot\\mathbf{p}$"}.
      </p>

      {/* ── WALKTHROUGH ── */}
      <h2 id="stack-walkthrough">Matrix Stack Walkthrough — Room Hierarchy</h2>
      <p>
        Traversing the room hierarchy step-by-step, showing the stack state at
        each step:
      </p>
      <ol>
        <li>
          <strong>Push room transform.</strong>
          <br />
          Stack: {"$[M_{\\text{room}}]$"}
        </li>
        <li>
          <strong>Push copy, multiply by table transform.</strong>
          <br />
          Stack: {"$[M_{\\text{room}}\\cdot M_{\\text{table}},\\; M_{\\text{room}}]$"}
        </li>
        <li>
          <strong>Push copy, multiply by saucer transform.</strong>
          <br />
          Stack:{" "}
          {"$[M_{\\text{room}}\\cdot M_{\\text{table}}\\cdot M_{\\text{saucer}},\\; M_{\\text{room}}\\cdot M_{\\text{table}},\\; M_{\\text{room}}]$"}
        </li>
        <li>
          <strong>Push copy, multiply by cup transform.</strong>
          <br />
          Stack top:{" "}
          {"$M_{\\text{room}}\\cdot M_{\\text{table}}\\cdot M_{\\text{saucer}}\\cdot M_{\\text{cup}}$"}
          {" "}— this is the cup's local-to-world matrix. Render the cup.
        </li>
        <li>
          <strong>Pop.</strong> Stack reverts to the saucer level. Process any
          other children of saucer, then pop again. Continue until all nodes
          are processed.
        </li>
      </ol>
      <p>
        Each pop restores the parent context, so sibling subtrees are processed
        independently with the correct accumulated transform.
      </p>

      {/* ── TRANSFORMING GEOMETRY ── */}
      <h2 id="transforming-geometry">Transforming Geometry</h2>
      <p>
        To transform a triangle mesh point {"$\\mathbf{p}_i$"} (stored in
        homogeneous form as {"$(x_i, y_i, z_i, 1)^T$"}) to world coordinates,
        multiply by the top of the matrix stack at that node:
      </p>
      <F>
        {"$$\\mathbf{p}_{\\text{world}} = M_{\\text{room}} \\cdot M_{\\text{table}} \\cdot M_{\\text{saucer}} \\cdot M_{\\text{cup}} \\cdot \\mathbf{p}_i$$"}
      </F>
      <p>
        The transformation matrices are accumulated as the scene graph is
        traversed and the matrix stack is updated, so by the time a leaf node
        is reached, the stack top already holds the full local-to-world
        product.
      </p>

      {/* ── IMMEDIATE MODE ── */}
      <h2 id="immediate-mode">Building Transformations via Immediate-Mode API</h2>
      <p>
        A classic immediate-mode graphics API (e.g. OpenGL 1.x) exposes the
        matrix stack directly. To build {"$M_{\\text{object}} = T\\cdot R\\cdot S$"}:
      </p>
      <pre>{`loadIdentity();       // M = I
translate(tx, ty, tz); // M = M · T  →  M = T
rotate(angle, axis);   // M = M · R  →  M = T·R
scale(sx, sy, sz);     // M = M · S  →  M = T·R·S`}</pre>
      <F>
        {[
          "After each call: $M_{\\text{new}} = M_{\\text{current}} \\cdot T_{\\text{call}}$",
          "Final result: $M_{\\text{object}} = T \\cdot R \\cdot S$",
        ]}
      </F>
      <p>
        Combined with <code>pushMatrix</code> / <code>popMatrix</code>, this
        lets each node set up its local transform without affecting its
        siblings.
      </p>

      {/* ── DECLARATIVE ── */}
      <h2 id="declarative">Declarative Scene Graphs</h2>
      <p>
        Rather than building transforms imperatively at runtime, a scene graph
        can be <strong>declared</strong> in a data format (JSON, visual editor,
        etc.) that specifies geometry, local TRS transforms, and parent/child
        relationships.
      </p>
      <p>Processing pipeline:</p>
      <ol>
        <li>
          Parse the scene graph structure before rendering
        </li>
        <li>
          Compute and <strong>cache</strong> local-to-world and world-to-local
          (inverse) transformation matrices for every node — using matrix-stack
          logic internally
        </li>
        <li>
          The rendering pass consumes the precomputed transforms directly,
          avoiding redundant recalculation each frame
        </li>
      </ol>
      <p>
        The matrix-stack logic is abstracted away from the developer, but the
        same accumulation principle applies.
      </p>

      {/* ── INVERSE ── */}
      <h2 id="inverse">Inverse Transforms in Scene Graphs</h2>
      <p>
        Inverse transforms enable operations in different coordinate spaces
        within the hierarchy. Key use cases:
      </p>
      <ul>
        <li>
          Converting world-space rays into a node's <strong>local space</strong>{" "}
          for simpler intersection tests (Object Instancing)
        </li>
        <li>
          Moving geometry from one node's coordinate system to another's
        </li>
      </ul>
      <p>
        Challenges: each inverse must be computed and stored alongside the
        forward transform, and applied in <em>reverse order</em>. Efficient
        handling is important for dynamic or complex scenes.
      </p>
      <F>
        {"$$M_{\\text{world-to-local}} = M_{\\text{local-to-world}}^{-1}$$"}
      </F>

      {/* ── INVERSE APPROACHES ── */}
      <h2 id="inverse-approaches">Approaches to Tracking Inverse Transforms</h2>

      <h3>1. Determinant-Based Inversion (Standard)</h3>
      <p>
        Precompute {"$M^{-1}$"} of the full accumulated forward transform at
        each node using the adjugate matrix method. Works for any invertible
        transformation regardless of composition. Simple to implement — the
        status quo approach.
      </p>

      <h3>2. Parallel Inverse Stack (Alternative)</h3>
      <p>
        Track each transformation operation (T, R, S) individually, compute
        its inverse, and push it onto a parallel inverse stack in{" "}
        <em>reverse order</em>. Potentially more efficient in specific cases
        but uncommon in practice.
      </p>
      <F>
        {[
          "Forward: $M = T\\cdot R\\cdot S$ (pushed left-to-right)",
          "Inverse: $M^{-1} = S^{-1}\\cdot R^{-1}\\cdot T^{-1}$ (pushed right-to-left onto inverse stack)",
        ]}
      </F>

      {/* ── FORWARD + INVERSE STACK ── */}
      <h2 id="forward-inverse-stack">Forward + Inverse Stack</h2>
      <p>
        When using a matrix stack with a scene graph, there are two objectives:
      </p>
      <ul>
        <li>
          <strong>Forward stack</strong> — accumulate local-to-world transforms
          as you traverse <em>down</em> the graph
        </li>
        <li>
          <strong>Inverse stack</strong> — compute and store world-to-local
          transforms (either via determinant inversion of the forward matrix,
          or via a parallel inverse stack)
        </li>
      </ul>
      <p>
        For the determinant approach, after computing the forward matrix at
        each node, invert it and cache the result. No parallel stack is needed.
      </p>

      {/* ── CROSS-LEVEL ── */}
      <h2 id="cross-level">Working Across Scene Graph Levels</h2>
      <p>
        To move geometry from one node's local space into another node's local
        space (e.g. expressing the coffee cup in the fruit bowl's coordinate
        system):
      </p>
      <ol>
        <li>
          Compute the cup's <strong>local-to-world</strong> matrix by
          multiplying all ancestor transforms up to the root:
          <br />
          {"$M_{\\text{cup\\_to\\_world}} = [\\ldots\\text{ancestors}\\ldots]\\cdot M_{\\text{cup}}$"}
        </li>
        <li>
          Compute the bowl's <strong>local-to-world</strong> matrix similarly:
          <br />
          {"$M_{\\text{bowl\\_to\\_world}} = [\\ldots\\text{ancestors}\\ldots]\\cdot M_{\\text{bowl}}$"}
        </li>
        <li>
          Invert the cup's local-to-world to get cup's <strong>world-to-local</strong>:
          <br />
          {"$M_{\\text{cup\\_world\\_to\\_local}} = M_{\\text{cup\\_to\\_world}}^{-1}$"}
        </li>
        <li>
          Compose to get the cup expressed in the bowl's space:
          <br />
          {"$M_{\\text{cup\\_in\\_bowl\\_space}} = M_{\\text{cup\\_world\\_to\\_local}} \\cdot M_{\\text{bowl\\_to\\_world}}$"}
        </li>
      </ol>
      <F>
        {"$$M_{A \\to B} = M_{A \\to \\text{world}}^{-1} \\cdot M_{B \\to \\text{world}}$$"}
      </F>

      <h3>Why Not Transform via Common Ancestor?</h3>
      <p>
        An alternative would be to find the lowest common ancestor and
        transform through it. In theory this could skip intermediate matrices,
        but in practice it is <strong>never used</strong> because:
      </p>
      <ul>
        <li>Requires dynamically identifying the common ancestor — added complexity</li>
        <li>Requires graph traversal — {"$O(h)$"} overhead where {"$h$"} is tree height</li>
        <li>Misses precomputed forward/inverse transforms — wastes cached work</li>
      </ul>
      <p>
        The standard approach (transform to world space, then invert to the
        target local space) is simpler, uses precomputed matrices, and is
        universally preferred.
      </p>

      {/* ── SUMMARY ── */}
      <h2 id="summary">Summary</h2>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "1rem", fontSize: "0.88rem" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
            <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontFamily: "system-ui", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>Concept</th>
            <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontFamily: "system-ui", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>Role</th>
          </tr>
        </thead>
        <tbody>
          {[
            ["Scene Graph (DAG)", "Encodes hierarchical spatial relationships; parent transforms propagate to children"],
            ["Local-to-World Matrix", "Product of all ancestor transforms from root to node"],
            ["Matrix Stack", "Maintains cumulative transform context during scene graph traversal"],
            ["pushMatrix / popMatrix", "Save/restore transform context when entering/leaving a subtree"],
            ["Right-multiplication", "$M_{\\text{new}} = M_{\\text{current}} \\cdot T$ — local transforms applied before parents"],
            ["Inverse (world-to-local)", "Needed for ray-object intersection in local space, cross-level coordinate changes"],
            ["Declarative graph", "Scene defined as data; transforms precomputed and cached before the render pass"],
          ].map(([c, r], i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
              <td style={{ padding: "8px 12px", fontFamily: "system-ui", fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>{c}</td>
              <td style={{ padding: "8px 12px", color: "#374151" }}>{r}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SceneGraph;
